import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { IntlService } from '@progress/kendo-angular-intl';
import { BillableCaseAccumulatorResponseDto, BillableService } from 'src/app/core/service/billable.service';
import { Organization } from 'src/app/core/model/organization';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { Code } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import moment from 'moment';

@Component({
  standalone: false,
  selector: 'app-case-detail-add-billable',
  templateUrl: './case-detail-add-billable.component.html',
  styleUrls: ['./case-detail-add-billable.component.scss']
})
export class CaseDetailAddBillableComponent implements OnInit
{
  @Output() public added = new EventEmitter<any>();
  @Output() public cancelled: EventEmitter<any> = new EventEmitter();

  public currentMemberId: number;
  public currentCaseId: number;
  public caseBillableForm: any;
  public billableServiceHasManualAmount: boolean = null;

  public feeSchedules: Code[] = [];
  public feeCategories: Code[] = [];
  public allBillableServices: any[] = [];
  public billableServicesFiltered: any[] = [];
  public searchTerm: string;
  public maxDateOfService: Date;

  public organizations = [];
  public organizationsFiltered: Organization[] = [];
  public virtualOrganization: any = { itemHeight: 30 };

  private textServiceMaxExceededUnits: string;
  private textServiceMaxExceededAmount: string;
  private textServiceDurationExceeded: string;

  constructor(
    private billableService: BillableService,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService,
    private organizationService: OrganizationService,
    private fb: UntypedFormBuilder,
    public intl: IntlService,
    private translate: TranslateService,)
  {
  }

  public get feeSchedule(): UntypedFormControl
  {
    return this.caseBillableForm.get('feeSchedule');
  }

  public get feeCategory(): UntypedFormControl
  {
    return this.caseBillableForm.get('feeCategory');
  }

  public get billableServiceControl(): UntypedFormControl
  {
    return this.caseBillableForm.get('billableServiceControl');
  }

  public get dateOfService(): UntypedFormControl
  {
    return this.caseBillableForm.get('dateOfService');
  }

  public get units(): UntypedFormControl
  {
    return this.caseBillableForm.get('units');
  }

  public get totalAmount(): UntypedFormControl
  {
    return this.caseBillableForm.get('totalAmount');
  }

  public get diagnoses(): UntypedFormControl
  {
    return this.caseBillableForm.get('diagnoses');
  }

  public get organization(): UntypedFormControl
  {
    return this.caseBillableForm.get('organization');
  }

  @Input() public set caseId(caseId: number)
  {
    if (caseId !== undefined)
    {
      this.currentCaseId = caseId;
    }
  }

  @Input() public set memberId(memberId: number)
  {
    if (memberId !== undefined)
    {
      this.currentMemberId = memberId;
    }
  }

  public ngOnInit(): void
  {
    this.buildForm();
    this.maxDateOfService = moment().toDate();
    this.dictionaryService.getCachedFeeScheduless().subscribe({
      next: (data) =>
      {
        this.feeSchedules = data;
      }
    });
    this.dictionaryService.getCachedFeeCategories().subscribe({
      next: (data) =>
      {
        this.feeCategories = data;
      }
    });
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
        this.filterOrganizations('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.translate.get(['ACCUMULATOR-MAXIMUM-EXCEEDED-UNITS', 'ACCUMULATOR-MAXIMUM-EXCEEDED-AMOUNT', 'ACCUMULATOR-DURATION-EXCEEDED']).subscribe({
      next: (res) =>
      {
        this.textServiceMaxExceededUnits = res['ACCUMULATOR-MAXIMUM-EXCEEDED-UNITS'];
        this.textServiceMaxExceededAmount = res['ACCUMULATOR-MAXIMUM-EXCEEDED-AMOUNT'];
        this.textServiceDurationExceeded = res['ACCUMULATOR-DURATION-EXCEEDED'];
      }
    });
  }

  public cancelCaseBillable(): void
  {
    this.cancelled.emit();
    if (this.caseBillableForm)
    {
      this.caseBillableForm.reset();
    }
  }

  public async saveCaseBillable(): Promise<void>
  {
    let amount: number | null = null;

    try
    {
      if (this.billableServiceHasManualAmount === false)
      {
        let d: any;
        try
        {
          d = await lastValueFrom(this.billableService.getBillableItemRate(this.billableServiceControl.value.id, this.dateOfService.value));
        }
        catch (e: any)
        {
          if (e?.error?.message === 'transient.error-billable-item-rate-required')
          {
            this.messageHandlerService.displayError('BILLABLE-RATE-UNAVAILABLE');
          }
          else
          {
            this.messageHandlerService.displayError(e);
          }
          return;
        }
        if (this.totalAmount.value === null && d.rate !== null)
        {
          amount = this.units.value * d.rate;
        }
        else
        {
          amount = this.totalAmount.value;
        }
      }
      else
      {
        amount = this.totalAmount.value;
      }

      const billableCaseAccumulatorRequestDto = {
        memberId: this.currentMemberId,
        serviceDate: moment(this.dateOfService.value).format('YYYY-MM-DD'),
        excludeCaseBillableId: null,
        billableService: {
          id: this.billableServiceControl.value.id,
          units: this.units.value,
          manualAmount: amount
        }
      };

      const accumulatorCheckResult = await lastValueFrom(
        this.billableService.checkAccumulatorsByBillableService(billableCaseAccumulatorRequestDto)
      ) as BillableCaseAccumulatorResponseDto;

      let allValid = true;
      if (accumulatorCheckResult?.validateMaximum)
      {
        if (accumulatorCheckResult.remainingUnits < 0)
        {
          this.messageHandlerService.displayDirectMessage('error', this.textServiceMaxExceededUnits.replace('{{serviceName}}', accumulatorCheckResult.serviceName));
          allValid = false;
        }
        if (accumulatorCheckResult.remainingAmount < 0)
        {
          this.messageHandlerService.displayDirectMessage('error', this.textServiceMaxExceededAmount.replace('{{serviceName}}', accumulatorCheckResult.serviceName));
          allValid = false;
        }
        if (accumulatorCheckResult.durationExceeded)
        {
          this.messageHandlerService.displayDirectMessage('error',
            this.textServiceDurationExceeded.replace('{{serviceName}}', accumulatorCheckResult.serviceName));
          allValid = false;
        }
      }

      if (allValid)
      {
        const caseBillableAddDTO: any = {
          billableId: this.billableServiceControl.value.id,
          caseId: this.currentCaseId,
          billableDate: this.dateOfService.value.toISOString().substring(0, 10),
          units: this.units.value,
          billableServiceHasManualAmount: this.billableServiceHasManualAmount,
          amount: this.billableServiceHasManualAmount ? this.totalAmount.value : null,
          diagnoses: this.diagnoses.value,
          caseBillableStatus: 'UNBILLED',
          organizationId: this.organization.value.id
        };

        await lastValueFrom(this.billableService.addCaseBillable(this.currentCaseId, caseBillableAddDTO));
        this.messageHandlerService.displaySuccess('CASE-BILLABLE-SAVED-SUCCESSFULLY');
        this.added.emit();
      }
    }
    catch (error)
    {
      this.messageHandlerService.displayError(error);
    }
  }

  public filterBillableServices(filter: string): void
  {
    this.searchTerm = filter;
    this.billableServicesFiltered = this.allBillableServices
      .filter(x => x.name.toLowerCase().includes(filter.toLowerCase()));
  }

  public getBillableServices(): void
  {
    if (this.feeSchedule.value && this.feeCategory.value)
    {
      this.billableService.getBillableServicesByCategoryAndScheduleId(this.feeCategory.value.id, this.feeSchedule.value.id).subscribe({
        next: (services) =>
        {
          this.allBillableServices = services.filter(e => e.informationalOnly === false);
          this.filterBillableServices('');
          this.billableServiceControl.enable();
          this.billableServiceControl.updateValueAndValidity();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else
    {
      this.billableServiceControl.disable();
      this.billableServiceControl.setValue(null);
      this.billableServiceControl.updateValueAndValidity();
    }
  }

  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  public filterOrganizations(filter: string): void
  {
    this.organizationsFiltered = this.organizations
      .filter(fa => fa.name.toLowerCase().includes(filter.toLowerCase()));
  }

  private buildForm(): void
  {
    this.caseBillableForm = this.fb.group({
      organization: [null, Validators.required],
      feeSchedule: [null, Validators.required],
      feeCategory: [null, Validators.required],
      billableServiceControl: [null, Validators.required],
      dateOfService: [new Date(), Validators.required],
      units: [null, Validators.required],
      totalAmount: [null], // required when service is Manual Amount
      diagnoses: [null, Validators.required],
    });

    this.getBillableServices();
    this.caseBillableForm.get('feeSchedule').valueChanges.subscribe({ next: () => { this.getBillableServices(); } });
    this.caseBillableForm.get('feeCategory').valueChanges.subscribe({ next: () => { this.getBillableServices(); } });
    this.caseBillableForm.get('billableServiceControl').valueChanges.subscribe({
      next: (service: any) =>
      {
        const diagnosesControl = this.caseBillableForm.get('diagnoses');
        diagnosesControl.setValue(null);
        if (service !== null && service != undefined)
        {
          this.billableServiceHasManualAmount = service.manualAmount;
          this.billableService.calculateCaseBillableDiagnosesCodes(this.currentMemberId, service.id, this.dateOfService.value).subscribe({
            next: (d: any) =>
            {
              diagnosesControl.setValue(d.calculatedDiagnosisCodes);
            },
            error: (error) =>
            {
              this.messageHandlerService.displayError(error);
            }
          });
        }
        else
        {
          this.billableServiceHasManualAmount = null;
        }
        this.updateTotalAmountValidators();
      }
    });
    this.updateTotalAmountValidators();
  }

  private updateTotalAmountValidators(): void
  {
    const totalAmountControl = this.caseBillableForm.get('totalAmount');
    totalAmountControl.clearValidators();

    if (this.billableServiceHasManualAmount === true)
    {
      totalAmountControl.setValidators(Validators.required);
    }
    else
    {
      totalAmountControl.setValue(null);
    }

    totalAmountControl.updateValueAndValidity();
  }
}
